import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const empty = {
  name: '',
  category: '',
  badge: '',
  price: '',
  pricePerPound: '',
  poundSizes: [],
  image: '',
  imagePublicId: '',
  description: '',
  longDescription: '',
  serves: '',
  weight: '',
  flavours: '',
  delivery: '',
  customizable: false,
  featured: false,
  isAvailable: true,
};

export default function CakeForm({ initial, cakeId, onSaved, showToast }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url, publicId } = await api.uploadImage(file);
      // Clean up the old image if we're replacing one.
      if (form.imagePublicId) {
        api.deleteImage(form.imagePublicId).catch(() => {});
      }
      update('image', url);
      update('imagePublicId', publicId);
      showToast && showToast('Photo uploaded');
    } catch (err) {
      showToast && showToast(err.message, 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: form.price === '' ? undefined : Number(form.price),
        pricePerPound: Number(form.pricePerPound) || 0,
        poundSizes: form.poundSizes || [],
        flavours: form.flavours
          .split(',')
          .map(f => f.trim())
          .filter(Boolean),
      };

      if (cakeId) {
        await api.updateCake(cakeId, payload);
        showToast && showToast('Cake updated');
      } else {
        await api.createCake(payload);
        showToast && showToast('Cake added');
      }
      if (onSaved) onSaved();
      else navigate('/admin/cakes');
    } catch (err) {
      showToast && showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <div className="admin-form-group">
        <label>Cake Name</label>
        <input
          type="text"
          required
          value={form.name}
          onChange={e => update('name', e.target.value)}
        />
      </div>

      <div className="admin-form-row">
        <div className="admin-form-group">
          <label>Category</label>
          <input
            type="text"
            required
            placeholder="e.g. Signature Cake"
            value={form.category}
            onChange={e => update('category', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label>Price (PKR) (Fixed price if no pound sizes)</label>
          <input
            type="number"
            min="0"
            required={!form.pricePerPound}
            value={form.price}
            onChange={e => update('price', e.target.value)}
          />
        </div>
      </div>

      <div className="admin-form-row">
        <div className="admin-form-group">
          <label>Per Pound Charge (PKR) (Optional)</label>
          <input
            type="number"
            min="0"
            value={form.pricePerPound}
            onChange={e => update('pricePerPound', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label>Available Pound Sizes (e.g. 2, 2.5, 3...)</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
            {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map(size => (
              <label key={size} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                <input
                  type="checkbox"
                  checked={(form.poundSizes || []).includes(size)}
                  onChange={e => {
                    const checked = e.target.checked;
                    const currentSizes = form.poundSizes || [];
                    const newSizes = checked 
                      ? [...currentSizes, size].sort() 
                      : currentSizes.filter(s => s !== size);
                    update('poundSizes', newSizes);
                  }}
                />
                {size} lbs
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="admin-form-row">
        <div className="admin-form-group">
          <label>Badge (optional)</label>
          <input
            type="text"
            placeholder="e.g. Bestseller, New, Custom"
            value={form.badge}
            onChange={e => update('badge', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label>Delivery Note</label>
          <input
            type="text"
            placeholder="e.g. 2–3 days advance order"
            value={form.delivery}
            onChange={e => update('delivery', e.target.value)}
          />
        </div>
      </div>

      <div className="admin-form-row">
        <div className="admin-form-group">
          <label>Serves</label>
          <input
            type="text"
            placeholder="e.g. 8–10 people"
            value={form.serves}
            onChange={e => update('serves', e.target.value)}
          />
        </div>
        <div className="admin-form-group">
          <label>Weight</label>
          <input
            type="text"
            placeholder="e.g. 1.5 kg"
            value={form.weight}
            onChange={e => update('weight', e.target.value)}
          />
        </div>
      </div>

      <div className="admin-form-group">
        <label>Flavours (comma separated)</label>
        <input
          type="text"
          placeholder="e.g. Vanilla Chiffon, Red Velvet, Belgian Chocolate"
          value={form.flavours}
          onChange={e => update('flavours', e.target.value)}
        />
      </div>

      <div className="admin-form-group">
        <label>Short Description (shown on the cake card)</label>
        <textarea
          rows={2}
          value={form.description}
          onChange={e => update('description', e.target.value)}
        />
      </div>

      <div className="admin-form-group">
        <label>Full Description (shown in the Description tab)</label>
        <textarea
          rows={5}
          value={form.longDescription}
          onChange={e => update('longDescription', e.target.value)}
        />
        <p className="admin-hint">Separate paragraphs with a blank line.</p>
      </div>

      <div className="admin-form-group">
        <label>Photo</label>
        <div className="admin-photo-grid">
          {form.image && (
            <div className="admin-photo-thumb">
              <img src={form.image} alt="" />
              <button
                type="button"
                className="admin-photo-remove"
                onClick={() => { update('image', ''); update('imagePublicId', ''); }}
              >
                ×
              </button>
            </div>
          )}
          <label className="admin-photo-add">
            {uploading ? 'Uploading…' : '+ Add Photo'}
            <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      <div className="admin-form-group" style={{ display: 'flex', gap: 24 }}>
        <label className="admin-checkbox-row">
          <input
            type="checkbox"
            checked={form.isAvailable}
            onChange={e => update('isAvailable', e.target.checked)}
          />
          Available for order
        </label>
        <label className="admin-checkbox-row">
          <input
            type="checkbox"
            checked={form.customizable}
            onChange={e => update('customizable', e.target.checked)}
          />
          Customizable
        </label>
        <label className="admin-checkbox-row">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={e => update('featured', e.target.checked)}
          />
          Featured (used as homepage hero)
        </label>
      </div>

      <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
        {saving ? 'Saving…' : cakeId ? 'Save Changes' : 'Add Cake'}
      </button>
    </form>
  );
}
